import { useDispatch, useSelector } from "react-redux";
import { AppState, AppDispatch } from '../store';
import {
    addClassModule,
    removeClassModule,
    classListSuccessPayload,
    clearClass,
    createClass,
    fetchClassModuleList,
    fetchClassDistrictList,
    fetchingClassList,
    fetchListClassModules,
    fetchListClassModulesEvery,
    fetchProviceList,
    fetchWardList,
    fetchSchoolList,
    getClassDetail,
    updateClass,
    addClassStudents,
    removeClassStudents,
    downloadTemplateAccountClassList,
    downloadTemplateClassImport,
    importClassList,
    fetchClassSchoolModules,
    deleteClass,
} from "../store/actions/classesActions";
import { clearTemplateClassFile, clearClassSchoolList, clearClassCreation, clearClassModuleList, clearImportClassTemplateFile, clearImportedClassListPreview, clearClassSchoolModuleOptions, setDeleteClassSuccess } from "@store/reducers/classesReducer";

type classDataDetailType = Omit<
    classListSuccessPayload,
    'id' | 'status' | 'created_date' | 'updated_date'
>;

/** Partial class PATCH (e.g. module_ids-only updates from ListModule). */
export type classUpdatePayloadType = Partial<classDataDetailType> & {
    module_ids?: number[];
    __skipRedirect?: boolean;
    /** Update from the class-list modal: keep the user on the current page. */
    __skipNavigation?: boolean;
};

type listClassModuleProps = {
    pageSize?: number,
    page?: number,
    status?: 0 | 1,
    classId?: number
}

export const useClass = () => {
    const dispatch: AppDispatch = useDispatch();
    const classList = useSelector((state: AppState) => state.class.classList)
    const classCreation = useSelector((state: AppState) => state.class.classCreation)
    const districList = useSelector((state: AppState) => state.class.districtList)
    const wardList = useSelector((state: AppState) => state.class.wardList)
    const schoolList = useSelector((state: AppState) => state.class.schoolList)
    const schoolListCount = useSelector((state: AppState) => state.class.schoolListCount)
    const loading = useSelector((state: AppState) => state.class.loading)
    const success = useSelector((state: AppState) => state.class.success)
    const classModules = useSelector((state: AppState) => state.class.classModules)
    const loadingDistrict = useSelector((state: AppState) => state.class.loadingDistrict)
    const loadingWard = useSelector((state: AppState) => state.class.loadingWard)
    const loadingSchool = useSelector((state: AppState) => state.class.loadingSchool)
    const classModuleList = useSelector((state: AppState) => state.class.classModuleList)
    const addLoading = useSelector((state: AppState) => state.class.addLoading)
    const removeLoading = useSelector((state: AppState) => state.class.removeLoading)
    const loadingDownloadTemplateClass = useSelector((state: AppState) => state.class.loadingDownloadTemplateClass)
    const templateClassFile = useSelector((state: AppState) => state.class.templateClassFile)
    const loadingDownloadImportClassTemplate = useSelector((state: AppState) => state.class.loadingDownloadImportClassTemplate)
    const importClassTemplateFile = useSelector((state: AppState) => state.class.importClassTemplateFile)
    const loadingImportClassList = useSelector((state: AppState) => state.class.loadingImportClassList)
    const importedClassListPreview = useSelector((state: AppState) => state.class.importedClassListPreview)
    const importClassListSuccess = useSelector((state: AppState) => state.class.importClassListSuccess)
    const classSchoolModuleOptions = useSelector((state: AppState) => state.class.classSchoolModuleOptions)
    const loadingClassSchoolModules = useSelector((state: AppState) => state.class.loadingClassSchoolModules)
    const loadingDeleteClass = useSelector((state: AppState) => state.class.loadingDeleteClass)
    const deleteClassSuccess = useSelector((state: AppState) => state.class.deleteClassSuccess)
    const loadingFetchClassModules = useSelector((state: AppState) => state.class.loadingFetchClassModules)
    const fetchClassData = (filters: any) => {
        dispatch(fetchingClassList(filters))
    }

    const createClassAction = (data: classDataDetailType): void => {
        dispatch(createClass(data))
    }

    const getClassDetailAction = (id?: string) => {
        dispatch(getClassDetail(id))
    }

    const clearClassAction = () => {
        dispatch(clearClass())
    }

    const fetchListClassModulesAction = (page: number, pageSize: number, classId: number, status?: number | null) => {
        dispatch((fetchListClassModules as any)({ page, pageSize, classId, status }))
    }

    const fetchListClassModulesEveryAction = (page: number, pageSize: number, classId: number) => {
        dispatch((fetchListClassModulesEvery as any)({ page, pageSize, classId }))
    }

    const updateClassAction = (data: classUpdatePayloadType, id: string): void => {
        dispatch((updateClass as any)(data, id))
    }

    const provinceList = useSelector((state: AppState) => state.class.provinceList)

    const fetchingProvinceList = () => {
        dispatch(fetchProviceList())
    }

    const fetchDistrictListAction = (provinceId: string) => {
        dispatch(fetchClassDistrictList(provinceId))
    }

    const fetchWardListAction = (districtId: string) => {
        dispatch(fetchWardList(districtId))
    }

    const fetchSchoolListAction = (wardId: string, districtId: string, provinceId: string, newWardId?: string, newProvinceId?: string, search?: string, page?: number, page_size?: number) => {
        dispatch(fetchSchoolList(wardId, districtId, provinceId, newWardId, newProvinceId, search, page, page_size))
    }

    const fetchClassModuleListAction = (classId: string) => {
        dispatch(fetchClassModuleList(classId))
    }

    const fetchClassSchoolModulesAction = (classId: string | number): void => {
        dispatch(fetchClassSchoolModules(classId))
    }

    const clearClassSchoolModuleOptionsAction = (): void => {
        dispatch(clearClassSchoolModuleOptions())
    }

    const addClassModuleAction = (moduleIds: string[], schoolId: string) => {
        dispatch(addClassModule(moduleIds, schoolId))
    }

    const removeClassModuleAction = (moduleIds: string[], schoolId: string) => {
        dispatch(removeClassModule(moduleIds, schoolId))
    }

    const addClassStudentsAction = (studentIds: string[], classId: string) => {
        dispatch(addClassStudents(studentIds, classId))
    }

    const removeClassStudentsAction = (studentIds: string[], classId: string) => {
        dispatch(removeClassStudents(studentIds, classId))
    }

    const downloadTemplateAccountClassListAction = (classId: number, schoolId: number) => {
        dispatch(downloadTemplateAccountClassList(classId, schoolId))
    }

    const clearTemplateClassFileAction = () => {
        dispatch(clearTemplateClassFile())
    }
    const downloadTemplateClassImportAction = () => {
        dispatch(downloadTemplateClassImport())
    }
    const importClassListAction = (file: File) => {
        dispatch(importClassList(file))
    }
    const clearImportClassTemplateFileAction = () => {
        dispatch(clearImportClassTemplateFile())
    }
    const clearImportedClassListPreviewAction = () => {
        dispatch(clearImportedClassListPreview())
    }
    const clearClassSchoolListAction = () => {
        dispatch(clearClassSchoolList())
    }
    const clearClassModuleListAction = () => {
        dispatch(clearClassModuleList())
    }
    const clearClassDetailsAction = () => {
        dispatch(clearClassCreation())
    }

    const deleteClassAction = (id: string): void => {
        dispatch(deleteClass({ id }))
    }

    const resetDeleteClassSuccessAction = (): void => {
        dispatch(setDeleteClassSuccess(false))
    }

    return {
        fetchClassData,
        classList,
        createClassAction,
        getClassDetailAction,
        loading,
        success,
        classCreation,
        updateClassAction,
        clearClassAction,
        fetchListClassModulesAction,
        fetchListClassModulesEveryAction,
        classModules,
        provinceList,
        fetchingProvinceList,
        fetchDistrictListAction,
        fetchWardListAction,
        fetchSchoolListAction,
        districList,
        wardList,
        schoolList,
        schoolListCount,
        loadingDistrict,
        loadingWard,
        loadingSchool,
        classModuleList,
        fetchClassModuleListAction,
        deleteClassAction,
        loadingDeleteClass,
        deleteClassSuccess,
        resetDeleteClassSuccessAction,
        loadingFetchClassModules,
        addClassModuleAction,
        removeClassModuleAction,
        addClassStudentsAction,
        removeClassStudentsAction,
        addLoading,
        removeLoading,
        loadingDownloadTemplateClass,
        templateClassFile,
        loadingDownloadImportClassTemplate,
        importClassTemplateFile,
        loadingImportClassList,
        importedClassListPreview,
        importClassListSuccess,
        downloadTemplateAccountClassListAction,
        clearTemplateClassFileAction,
        downloadTemplateClassImportAction,
        importClassListAction,
        clearImportClassTemplateFileAction,
        clearImportedClassListPreviewAction,
        clearClassSchoolListAction,
        clearClassDetailsAction,
        clearClassModuleListAction,
        fetchClassSchoolModulesAction,
        clearClassSchoolModuleOptionsAction,
        classSchoolModuleOptions,
        loadingClassSchoolModules,
    };
};
